import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useQuery } from "@tanstack/react-query";
import { ChevronDown, HelpCircle } from "lucide-react";
import { ToggleGroup, EmptyState, SearchField, focusRingInsetClass } from "@/components/ui";
import { matchesTextQuery } from "@/lib/textSearch";
import { ListResultsCard } from "@/components/common";
import { SELLER_FAQ_ITEMS } from "@/constants/sellerFaqConfig";
import {
  isSellerFaqItemVisible,
  sellerFaqPaymentContext,
} from "@/lib/sellerFaqVisibility";
import { activityService } from "@/services/activityService";
import { useAuthStore } from "@/store/authStore";
import { UserRole } from "@/types";

import { cn } from "@/lib/utils";

interface FAQItem {
  id: string;
  question: string;
  answer: string;
  category: string;
}

export function FAQSection() {
  const { t } = useTranslation("sellerFaq");
  const { t: tSupport } = useTranslation("support");
  const { t: tCommon } = useTranslation("common");
  const [query, setQuery] = useState("");
  const { user, getCurrentUserId, isSalesAgent } = useAuthStore();
  const userId = getCurrentUserId();
  const role = user?.role;
  const isSeller = role === UserRole.SELLER;

  const { data: activities = [], isSuccess: activitiesLoaded } = useQuery({
    queryKey: ["activities"],
    queryFn: () => activityService.getAll(),
    enabled: !!userId && isSeller && !isSalesAgent(),
  });

  const paymentContext = useMemo(
    () => sellerFaqPaymentContext(activities),
    [activities],
  );

  const faqItems = useMemo<FAQItem[]>(() => {
    return SELLER_FAQ_ITEMS.filter((item) =>
      isSellerFaqItemVisible(
        item,
        role,
        paymentContext,
        !isSeller || activitiesLoaded,
      ),
    ).map((item) => ({
      id: item.id,
      category: t(`categories.${item.categoryKey}`),
      question: t(`items.${item.id}.question`),
      answer: t(`items.${item.id}.answer`),
    }));
  }, [activitiesLoaded, isSeller, paymentContext, role, t]);

  const categories = [...new Set(faqItems.map((item) => item.category))];
  const [openId, setOpenId] = useState<string | null>(null);
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);

  const filteredItems = faqItems.filter(
    (item) =>
      (!selectedCategory || item.category === selectedCategory) &&
      matchesTextQuery(query, [item.question, item.answer]),
  );

  return (
    <div className="space-y-4">
      <SearchField
        value={query}
        onChange={setQuery}
        placeholder={tSupport("faqSearchPlaceholder")}
        searchLabel={tCommon("search")}
        clearSearchLabel={tCommon("clearSearch")}
      />
      <ToggleGroup
        value={selectedCategory ?? "all"}
        onChange={(value) =>
          setSelectedCategory(value === "all" ? null : value)
        }
        ariaLabel={tSupport("faqAll")}
        options={[
          { value: "all", label: tSupport("faqAll") },
          ...categories.map((category) => ({
            value: category,
            label: category,
          })),
        ]}
      />

      <ListResultsCard
        hideHeader
        contentClassName="p-0"
        isEmpty={filteredItems.length === 0}
        emptyState={
          <EmptyState
            size="md"
            icon={HelpCircle}
            title={tSupport("faqNone")}
          />
        }
      >
        <div className="divide-y divide-surface-200/70">
          {filteredItems.map((item) => {
            const isOpen = openId === item.id;
            return (
              <div
                key={item.id}
                className={cn(
                  "transition-colors",
                  isOpen && "bg-surface-50/70 dark:bg-surface-100/30",
                )}
              >
                <button
                  type="button"
                  onClick={() => setOpenId(isOpen ? null : item.id)}
                  aria-expanded={isOpen}
                  className={cn(
                    "flex w-full items-center justify-between gap-4 px-5 py-3.5 text-left transition-colors focus-visible:bg-surface-50",
                    focusRingInsetClass,
                    !isOpen && "hover:bg-surface-50 dark:hover:bg-surface-100/50",
                  )}
                >
                  <span className="flex items-center gap-3">
                    <HelpCircle
                      className={cn(
                        "h-4 w-4 shrink-0",
                        isOpen ? "text-primary-600" : "text-surface-400",
                      )}
                      aria-hidden
                    />
                    <span
                      className={cn(
                        "text-sm font-medium",
                        isOpen ? "text-surface-900" : "text-surface-700",
                      )}
                    >
                      {item.question}
                    </span>
                  </span>
                  <ChevronDown
                    className={cn(
                      "h-4 w-4 shrink-0 text-surface-400 transition-transform duration-200",
                      isOpen && "rotate-180",
                    )}
                    aria-hidden
                  />
                </button>
                {isOpen && (
                  <div className="pb-4 pl-12 pr-5">
                    <p className="whitespace-pre-line text-sm leading-relaxed text-surface-600">
                      {item.answer}
                    </p>
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </ListResultsCard>
    </div>
  );
}
